import React, { useState, useMemo } from 'react';
import { Search, Plus, Loader2, AlertCircle, MoreVertical, Edit2, Trash2, MessageSquare, Users, Clock } from 'lucide-react';


/* ─── Helpers ─────────────────────────────────────────────────────────── */
const getThreadId = (thread) => thread?.thread_id || thread?.id || thread?._id;

const getThreadTitle = (thread, loggedInUserId) => {
    if (!thread) return 'Untitled';
    if (thread.chat_category === 'personal' || thread.is_group === false) {
        if (thread.participants && Array.isArray(thread.participants)) {
            const other = thread.participants.find(p => String(p.user_id) !== String(loggedInUserId));
            if (other?.display_name) return other.display_name.replace(/raci/i, 'RACI');
            if (thread.participants.length === 1 && String(thread.participants[0].user_id) === String(loggedInUserId))
                return ((thread.participants[0].display_name || 'You') + ' (You)').replace(/raci/i, 'RACI');
        }
    }
    const raw = thread.thread_name || thread.display_name || thread.title || thread.name ||
        (thread.scope_type === 'RFP_GENERAL' ? 'Everyone on this proposal' : 'Conversation');
    return raw.replace(/^Section\s*\(/i, '').replace(/\)$/, '').trim().replace(/raci/i, 'RACI');
};

const getLastPreview = (thread) =>
    thread?.last_message?.body || thread?.last_message?.content ||
    thread?.last_message_preview || thread?.preview || null;

const getLastTime = (thread) => {
    const raw = thread?.last_message?.created_at || thread?.updated_at || thread?.created_at;
    if (!raw) return '';
    try {
        const d = new Date(raw.includes('T') && !raw.endsWith('Z') ? raw + 'Z' : raw);
        if (isNaN(d)) return '';
        const now = new Date();
        const diffMs = now - d;
        const diffMin = Math.floor(diffMs / 60000);
        const diffH = Math.floor(diffMin / 60);
        const diffD = Math.floor(diffH / 24);
        if (diffMin < 1) return 'now';
        if (diffMin < 60) return `${diffMin}m`;
        if (diffH < 24) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
        if (diffD === 1) return 'Yesterday';
        if (diffD < 7) return d.toLocaleDateString([], { weekday: 'short' });
        return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } catch { return ''; }
};

const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
};

// Deterministic avatar color from name string
const getAvatarColor = (name) => {
    if (!name) return { bg: '#64748B', text: '#fff' };
    const colors = [
        { bg: '#FF5526', text: '#fff' }, // blue
        { bg: '#8B5CF6', text: '#fff' }, // purple
        { bg: '#10B981', text: '#fff' }, // green
        { bg: '#F59E0B', text: '#fff' }, // amber
        { bg: '#EF4444', text: '#fff' }, // red
        { bg: '#06B6D4', text: '#fff' }, // cyan
        { bg: '#EC4899', text: '#fff' }, // pink
        { bg: '#6366F1', text: '#fff' }, // indigo
        { bg: '#14B8A6', text: '#fff' }, // teal
        { bg: '#F97316', text: '#fff' }, // orange
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
};

const isGroupThread = (t) =>
    String(t.chat_category || '').toLowerCase() === 'group' ||
    t.is_group === true ||
    t.scope_type === 'RFP_GENERAL' ||
    t.scope_type === 'RFP_SECTION';

const getSessionId = (s) => s?.session_id ?? s?.id ?? s?._id;
const getSessionTitle = (s) => s?.title ?? s?.name ?? s?.session_name ?? `Session ${(getSessionId(s) || '').slice(-6)}`.trim();

/* ─── Thread Card ─────────────────────────────────────────────────────── */
const ThreadCard = ({ thread, isActive, currentUserId, onClick }) => {
    const title = getThreadTitle(thread, currentUserId);
    const preview = getLastPreview(thread);
    const time = getLastTime(thread);
    const unread = thread.unread_count || 0;
    const isGroup = isGroupThread(thread);
    const participantCount = thread.participant_count || thread.participants?.length || 0;
    const avatarColor = getAvatarColor(title);
    const initials = getInitials(title);

    // Online indicator — only for DMs
    const otherParticipant = !isGroup && thread.participants
        ? thread.participants.find(p => String(p.user_id) !== String(currentUserId))
        : null;
    const isOnline = otherParticipant?.is_online || otherParticipant?.status === 'online';

    return (
        <button
            className={`vch-thread-card ${isActive ? 'active' : ''}`}
            onClick={onClick}
            title={title}
        >
            {/* Avatar */}
            <div className="vch-tc-avatar-wrap">
                <div
                    className="vch-tc-avatar"
                    style={{ background: avatarColor.bg, color: avatarColor.text }}
                >
                    {isGroup
                        ? <Users size={14} strokeWidth={2} />
                        : initials}
                </div>
                {!isGroup && isOnline && <span className="vch-tc-online-dot" />}
            </div>

            {/* Text */}
            <div className="vch-tc-text">
                <div className="vch-tc-top-row">
                    <span className="vch-tc-title">{title}</span>
                    {time && <span className="vch-tc-time">{time}</span>}
                </div>
                <div className="vch-tc-bottom-row">
                    <span className="vch-tc-preview">
                        {isGroup && participantCount > 0
                            ? `${participantCount} Member${participantCount !== 1 ? 's' : ''}`
                            : (preview || '\u00A0')}
                    </span>
                    {unread > 0 && (
                        <span className="vch-tc-badge">{unread > 99 ? '99+' : unread}</span>
                    )}
                </div>
            </div>
        </button>
    );
};

/* ─── AI Session Item ─────────────────────────────────────────────────── */
const SessionCard = ({ session, isActive, onClick, onDelete, onRename }) => {
    const sid = getSessionId(session);
    const title = getSessionTitle(session);
    const [menuOpen, setMenuOpen] = useState(false);

    return (
        <div className={`vch-thread-card ${isActive ? 'active' : ''}`} style={{ position: 'relative' }}>
            <button className="vch-sc-main" onClick={onClick} title={title}>
                <div className="vch-tc-avatar-wrap">
                    <div className="vch-tc-avatar" style={{ background: '#FF5526' }}>
                        <MessageSquare size={13} strokeWidth={2} />
                    </div>
                </div>
                <div className="vch-tc-text">
                    <span className="vch-tc-title">{title}</span>
                </div>
            </button>
            {(onDelete || onRename) && (
                <div style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', zIndex: 20 }}>
                    <button
                        className="vch-sc-menu-btn"
                        onClick={e => { e.stopPropagation(); setMenuOpen(v => !v); }}
                    >
                        <MoreVertical size={13} />
                    </button>
                    {menuOpen && (
                        <>
                            <div style={{ position: 'fixed', inset: 0, zIndex: 998 }} onClick={() => setMenuOpen(false)} />
                            <div className="vch-sc-dropdown">
                                {onRename && (
                                    <button className="vch-sc-dropdown-item" onClick={e => {
                                        e.stopPropagation(); setMenuOpen(false);
                                        const t = window.prompt('Rename session:', title);
                                        if (t?.trim()) onRename(sid, t.trim());
                                    }}>
                                        <Edit2 size={11} /> Rename
                                    </button>
                                )}
                                {onDelete && (
                                    <button className="vch-sc-dropdown-item danger" onClick={e => {
                                        e.stopPropagation(); setMenuOpen(false);
                                        if (window.confirm('Delete this session?')) onDelete(sid);
                                    }}>
                                        <Trash2 size={11} /> Delete
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

/* ─── Main Sidebar ────────────────────────────────────────────────────── */
const ChatSidebar = ({
    isOpen,
    onToggleSidebar,
    activeTab,
    threads = [],
    selectedThreadId,
    aiSessions = [],
    selectedSessionId,
    onSelectSession,
    onDeleteSession,
    onRenameSession,
    onCreateSession,
    loadingSessions = false,
    sessionsError = null,
    loadingThreads = false,
    onSelectThread,
    onOpenWizard,
    hasRfpContext,
    currentUserId,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [filter, setFilter] = useState('all');

    /* ── Filtered threads ── */
    const filteredThreads = useMemo(() => {
        const q = searchQuery.toLowerCase().trim();
        let list = q ? threads.filter(t =>
            getThreadTitle(t, currentUserId).toLowerCase().includes(q) ||
            (getLastPreview(t) || '').toLowerCase().includes(q)
        ) : threads;
        if (filter === 'groups') list = list.filter(isGroupThread);
        if (filter === 'dms') list = list.filter(t => !isGroupThread(t));
        return list;
    }, [threads, searchQuery, currentUserId, filter]);

    /* ── Filtered AI sessions ── */
    const filteredSessions = useMemo(() =>
        aiSessions.filter(s => getSessionTitle(s).toLowerCase().includes(searchQuery.toLowerCase())),
        [aiSessions, searchQuery]
    );

    const groupedSessions = useMemo(() => {
        const groups = { today: [], yesterday: [], week: [], older: [] };
        const dayMs = 86400000;
        const startToday = new Date().setHours(0, 0, 0, 0);
        filteredSessions.forEach(s => {
            const raw = s?.created_at ?? s?.updated_at ?? s?.timestamp;
            const t = raw ? new Date(raw).getTime() : 0;
            if (!t || isNaN(t)) { groups.older.push(s); return; }
            if (t >= startToday) groups.today.push(s);
            else if (t >= startToday - dayMs) groups.yesterday.push(s);
            else if (t >= startToday - 7 * dayMs) groups.week.push(s);
            else groups.older.push(s);
        });
        return groups;
    }, [filteredSessions]);

    return (
        <div className="vch-sidebar">
            {/* ── Header ── */}
            <div className="vch-sidebar-header">
                <button
                    className="vch-sidebar-toggle-btn"
                    onClick={onToggleSidebar}
                    title={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
                >
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
                    </svg>
                </button>
                {isOpen && <span className="vch-sidebar-title">Chats</span>}
                {isOpen && activeTab === 'team' && (
                    <button
                        className="vch-sidebar-new-btn"
                        onClick={onOpenWizard}
                        disabled={!hasRfpContext}
                        title="New Chat"
                    >
                        <Plus size={14} strokeWidth={2.5} />
                        <span>New Chat</span>
                    </button>
                )}
                {isOpen && activeTab === 'ai' && (
                    <button
                        className="vch-sidebar-new-btn"
                        onClick={onCreateSession}
                        title="New AI Session"
                    >
                        <Plus size={14} strokeWidth={2.5} />
                        <span>New Chat</span>
                    </button>
                )}
            </div>

            {/* ── Search ── */}
            <div className="vch-sidebar-search-wrap">
                <Search size={13} className="vch-sidebar-search-icon" />
                <input
                    type="text"
                    placeholder={activeTab === 'team' ? 'Search chats...' : 'Search history...'}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="vch-sidebar-search-input"
                />

            </div>

            {/* ── Tabs (team only) ── */}
            {activeTab === 'team' && (
                <div className="vch-sidebar-tabs">
                    {['all', 'groups', 'dms'].map(f => (
                        <button
                            key={f}
                            className={`vch-sidebar-tab ${filter === f ? 'active' : ''}`}
                            onClick={() => setFilter(f)}
                        >
                            {f === 'all' ? 'All' : f === 'groups' ? 'Groups' : 'Direct'}
                        </button>
                    ))}
                </div>
            )}

            {/* ── List ── */}
            <div className="vch-sidebar-list">
                {activeTab === 'team' ? (
                    <>
                        {loadingThreads && filteredThreads.length === 0 && (
                            <div className="vch-sidebar-empty">
                                <Loader2 size={16} className="vch-spin" />
                                <span>Loading chats...</span>
                            </div>
                        )}
                        {!loadingThreads && filteredThreads.length === 0 && (
                            <div className="vch-sidebar-empty">
                                <span>No conversations found.</span>
                            </div>
                        )}
                        {filteredThreads.length > 0 && (
                            <div className="vch-thread-section">
                                <div className="vch-thread-section-label">CHATS</div>
                                {filteredThreads.map((t, idx) => (
                                    <ThreadCard
                                        key={getThreadId(t) || idx}
                                        thread={t}
                                        isActive={String(getThreadId(t)) === String(selectedThreadId)}
                                        currentUserId={currentUserId}
                                        onClick={() => onSelectThread(getThreadId(t))}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {loadingSessions && (
                            <div className="vch-sidebar-empty">
                                <Loader2 size={16} className="vch-spin" />
                                <span>Loading sessions...</span>
                            </div>
                        )}
                        {sessionsError && !loadingSessions && (
                            <div className="vch-sidebar-empty" style={{ color: '#EF4444' }}>
                                <AlertCircle size={14} /> {sessionsError}
                            </div>
                        )}
                        {!loadingSessions && !sessionsError && filteredSessions.length === 0 && (
                            <div className="vch-sidebar-empty">No sessions found.</div>
                        )}
                        {[
                            { label: 'TODAY', items: groupedSessions.today },
                            { label: 'YESTERDAY', items: groupedSessions.yesterday },
                            { label: 'PREVIOUS 7 DAYS', items: groupedSessions.week },
                            { label: 'OLDER', items: groupedSessions.older },
                        ].map(({ label, items }) => items.length > 0 && (
                            <div key={label} className="vch-thread-section">
                                <div className="vch-thread-section-label">
                                    <Clock size={9} /> {label}
                                </div>
                                {items.map(s => (
                                    <SessionCard
                                        key={getSessionId(s)}
                                        session={s}
                                        isActive={String(getSessionId(s)) === String(selectedSessionId)}
                                        onClick={() => onSelectSession?.(getSessionId(s))}
                                        onDelete={onDeleteSession}
                                        onRename={onRenameSession}
                                    />
                                ))}
                            </div>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
};

export default ChatSidebar;